/* Core & libraries */
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import Container from "react-bootstrap/Container";
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router";

/* Helpers, constants, custom methods, internal libraries */
import { trackEvent } from "../../services/mixpanelUtil";
import { usePeopleService } from "../../services/peopleService";
import { useGlobalContext, useGlobalUpdateContext } from "../../contexts/globalContext";
import { useHasPermission } from "../../hooks/useHasPermission";
import { commasFormat } from "../../helpers/general";
import { useUserContext } from "contexts/userContext";
import { AIRLINES, configPeopleHeader, configPeopleHeaderTabs } from "../../constants/appConstants";
import { CREATE_GUEST_PROFILE } from "../../constants/routes";

/* Components */
import { ReactComponent as VerticalDotsSvg } from '../../resources/images/dots-vertical.svg';
import { ReactComponent as CsvSvg } from '../../resources/images/file-plus-02.svg';
import { ReactComponent as PersonSvg } from '../../resources/images/person.svg';
import { ReactComponent as DocumentTextSvg } from '../../resources/images/document-text.svg';
import { ReactComponent as ChevronDownSvg } from '../../resources/images/chevron_big_down.svg';
import UCButton from "../../components/buttons";
import PendingPeople from "../pendingPeople";
import InviteBulkCsvModal from "../modals/inviteBulkCsv";
import CreateTeamModal from "../modals/createTeamModal";
import BulkUploadGuestProfilesModal from "../modals/bulkUploadGuestProfilesModal";

/* Styles */
import styles from "./styles.js"

const PeoplePageHeader = ({getTeamsList, getGuestProfilesAction}) => {
  const theme = useTheme();
  const classes = styles({ theme });

  const location = useLocation();
  const navigate = useNavigate();

  const globalProperties  = useGlobalContext();
  const setGlobalProperties  = useGlobalUpdateContext();
  const { checkPermission } = useHasPermission();
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const { getSharedLink } = usePeopleService();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showUploadGuestProfilesModal, setShowUploadGuestProfilesModal] = useState(false);
  const [showPendingInvitationsModal, setPendingInvitationsModal] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);

  const { invitationCount, getPendingInviteCount, expiryDaysCount } = usePeopleService();
  const { pendingInviteResponse, getPendingInviteList, resendInvite, revokeInvite, isPendingFetching } = usePeopleService();

  const [pendingInvitations, setPending] = useState({
    page: 1,
    searchterm: "",
    sortby: "invitationDateTime",
    sortdirection: "dsc",
  })
  const [overlayOpen, setOverlayOpen] = useState(false)
  const [inviteDropdownOpen, setInviteDropdownOpen] = useState(false)
  const firstPopoverItemRef = useRef(null);
  const dotsButtonItemRef = useRef(null);
  const firstInviteDropdownRef = useRef(null);
  const inviteButtonRef = useRef(null);

  const activeTab = useMemo(() => (
    Object.entries(configPeopleHeaderTabs).find(([route]) =>
      location?.pathname.includes(route)
    )?.[1] || "people"
  ), [location?.pathname]);
  const config = configPeopleHeader[activeTab];

  useEffect(() => {
    if(!showPendingInvitationsModal) {
      setPending({
        page: 1,
        searchterm: "",
        sortby: "invitationDateTime",
        sortdirection: "dsc",
      })
    }
  }, [showPendingInvitationsModal])

  const handleBulkInviteCsv = () => {
    // Track the button click event
    trackEvent("Bulk Invite CSV Button Clicked", {});

    document.getElementById("root")?.click();
    setShowUploadModal(true);
  }

  const handleBulkUploadGuestProfilesCsv = () => {
    // Track the button click event
    trackEvent("Bulk upload guest profiles CSV Button Clicked", {});

    document.getElementById("root")?.click();    setShowUploadGuestProfilesModal(true);
  }

  const handleModalClick = async () =>{
    const response = await getSharedLink();
    if (response) {
      setGlobalProperties({
        ...globalProperties,
        invitePeopleModal: {
          showInvitePeopleModal: true,
          teamName: response.teamName,
          sharedLink: response.sharedLink,
          active: response.active,
          requiresDomain: response.requiresDomain
        }
      })
    }
  }

  const handlePendingModalClick = () =>{
    setPendingInvitationsModal(true)
  }

  const handleCreateTeamModalClick = () =>{
    setShowCreateTeamModal(true)
  }

  useEffect(() => {
    getPendingInviteCount();
    getPendingInviteList(pendingInvitations);
  }, [pendingInvitations, showPendingInvitationsModal, globalProperties.inviteCount, globalProperties?.inviteAuthorisedModal?.shellUserCount])

  const isActionAllowed = (buttonName) => {
    return !!config.actions.find((item) => item === buttonName);
  }

  const renderPopover = () => {
    return(
      <Popover className={classes.popoverStyle}>
        <div className={classes.popoverDiv}>
          {/* <div className={classes.popoverItem}>
              <ConnectSvg />
              <FormattedMessage id="peopleHeader.connectHr" />
            </div> */}
          {
            isActionAllowed("bulkUpload") &&
            <div
              className={classes.popoverItem}
              onClick={handleBulkInviteCsv}
              tabIndex={0} ref={firstPopoverItemRef}
              onKeyDown={handleKeyDown}
            >
              <CsvSvg/>
              <FormattedMessage id="peopleHeader.bulkInvite"/>
            </div>
          }
        </div>
      </Popover>
    )
  }

    const handleInvitePeopleClick = () => {
    document.getElementById("root")?.click();
    handleModalClick();
  }

  const renderInviteDropdown = () => (
    <Popover className={classes.popoverStyle}>
      <div className={classes.inviteDropdownDiv}>
        <div
          className={classes.popoverItem}
          onClick={handleInvitePeopleClick}
          tabIndex={0}
          ref={firstInviteDropdownRef}
          onKeyDown={handleKeyDown}
        >
          <PersonSvg/>
          <FormattedMessage id="people.inviteButton"/>
        </div>
        {
          isActionAllowed("bulkUpload") && checkPermission("F_INVITE_BULKUPLOAD") &&
          <div
            className={classes.popoverItem}
            onClick={handleBulkInviteCsv}
            tabIndex={0}
            onKeyDown={handleKeyDown}
          >
            <DocumentTextSvg/>
            <FormattedMessage id="peopleHeader.uploadCsv"/>
          </div>
        }
      </div>
    </Popover>
  )

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.currentTarget.click();
    }
  };

  return (
    <div className={classes.pageHeader}>
      <Container className={classes.container}>
        <Row>
        <Col xs={config.colSize} sm={12} md={12} lg={6} className="gx-0">
            <h1 className={classes.title} tabIndex={0}>
              <FormattedMessage id={config.titleId}/>
            </h1>
            {providerCode !== AIRLINES.AS && (
              <p className={classes.subtitle} tabIndex={0}>
                <FormattedMessage id={config.subtitleId}/>
              </p>
            )}
          </Col>
          <Col xs={config.colSize} sm={12} md={12} lg={6}>
            <div className={`${classes.actions} ${classes[config.btnClassName]}`}>
              {
                checkPermission("F_INVITE_BULKUPLOAD") && isActionAllowed("overlayButton") && !isActionAllowed("inviteUser") &&
                <OverlayTrigger
                    trigger="click"
                    rootClose
                    placement="left"
                    overlay={renderPopover()}
                    onEnter={() => setOverlayOpen(true)}
                    onExit={() => setOverlayOpen(false)}
                    onEntered={() => {
                      setTimeout(() => {
                        firstPopoverItemRef.current?.focus();
                      }, 0);
                    }}
                    onExited={() => {
                      setTimeout(() => {
                        dotsButtonItemRef.current?.focus();
                      }, 0);
                    }}
                  >
                  <div
                    className={`${classes.dotsStyle} ${overlayOpen ? classes.dotsOpenStyle : ""}`}
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    aria-haspopup="true"
                    role="button"
                    ref={dotsButtonItemRef}
                    >
                    <VerticalDotsSvg/>
                  </div>
                </OverlayTrigger>
              }

              <div className={classes.btnsDiv}>
                {
                  checkPermission("F_CREATE_GROUP") && isActionAllowed("createGroup") &&
                  <UCButton
                    type="primary"
                    onClick={() => handleCreateTeamModalClick()}
                    text={<FormattedMessage id="teams.create"/>}
                    className={classes.createTeamBtn}
                    tabIndex={0}
                  />
                }
                {
                  checkPermission("F_VIEW_PEOPLE_PENDING") && isActionAllowed("pendingInvites") &&
                  <UCButton
                    type="default"
                    className={classes.pendingInvitesBtn}
                    text={<FormattedMessage id="people.pendingInvites"/>}
                    onClick={() => handlePendingModalClick()}
                    children={invitationCount ?
                      <span className={classes.pendingInvitesNr}>
                              {commasFormat(invitationCount.toString())}
                            </span> : null
                    }
                    tabIndex={0}
                  />
                }
                {
                  checkPermission("F_INVITE_USER") && isActionAllowed("inviteUser") &&
                  <OverlayTrigger
                    trigger="click"
                    rootClose
                    placement="bottom-end"
                    overlay={renderInviteDropdown()}
                    onEnter={() => setInviteDropdownOpen(true)}
                    onExit={() => setInviteDropdownOpen(false)}
                    onEntered={() => {
                      setTimeout(() => {
                        firstInviteDropdownRef.current?.focus();
                      }, 0);
                    }}
                                        onExited={() => {
                      setTimeout(() => {
                        inviteButtonRef.current?.focus();
                      }, 0);
                    }}
                  >
                    <div ref={inviteButtonRef}>
                      <UCButton
                        type="primary"
                        className={`${classes.invBtn} ${inviteDropdownOpen ? classes.invBtnOpen : ""}`}
                        text={<FormattedMessage id="people.inviteButton"/>}
                        spinner={{
                          name: "invitePeopleButton",
                          size: 20,
                        }}
                        iconEnd={<ChevronDownSvg />}
                        iconClassName={`${classes.caretIcon} ${inviteDropdownOpen ? classes.caretIconOpen : ""}`}
                        tabIndex={0}
                      />
                    </div>
                  </OverlayTrigger>
                }
                {
                  checkPermission("F_GUEST_PROFILE_MANAGE") && isActionAllowed("uploadProfiles") &&
                  <UCButton
                    type="default"
                    className={classes.pendingInvitesBtn}
                    text={<FormattedMessage id="guest.uploadProfiles"/>}
                    onClick={handleBulkUploadGuestProfilesCsv}
                  />
                }
                {
                  checkPermission("F_GUEST_PROFILE_MANAGE") && isActionAllowed("createProfile") &&
                  <UCButton
                    type="primary"
                    className={classes.invBtn}
                    onClick={() => navigate(CREATE_GUEST_PROFILE)}
                    text={<FormattedMessage id="guest.createProfile"/>}
                  />
                }
              </div>
            </div>
          </Col>
        </Row>
        {
          isActionAllowed("pendingInvites") &&
          <PendingPeople
            showPendingInvitationsModal={showPendingInvitationsModal}
            setPendingInvitationsModal={setPendingInvitationsModal}
            pendingInviteResponse={pendingInviteResponse}
            pendingInvitations={pendingInvitations}
            setPending={setPending}
            resendInvite={resendInvite}
            revokeInvite={revokeInvite}
            isFetching={isPendingFetching}
            invitationCount={invitationCount}
            expiryDaysCount={expiryDaysCount}
          />
        }
        {
          isActionAllowed("bulkUpload") &&
          <InviteBulkCsvModal
            showUploadModal={showUploadModal}
            setShowUploadModal={setShowUploadModal}
            getPendingInviteCount={getPendingInviteCount}
            dotsButtonItemRef={dotsButtonItemRef}
          />
        }
        {
          isActionAllowed("createGroup") &&
          <CreateTeamModal
            showCreateTeamModal={showCreateTeamModal}
            setShowCreateTeamModal={setShowCreateTeamModal}
            getTeamsList={getTeamsList}
          />
        }
        {
          isActionAllowed("uploadProfiles") &&
          <BulkUploadGuestProfilesModal
            showModal={showUploadGuestProfilesModal}
            setShowModal={setShowUploadGuestProfilesModal}
            getGuestProfilesAction={getGuestProfilesAction}
          />
        }
      </Container>
    </div>
  )
}

export default PeoplePageHeader;
